import { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { api } from '../lib/api';
import type { CountyTransferRecord } from '../types';

const COUNTIES = ['San Bernardino', 'Riverside', 'Los Angeles', 'Orange'];

function formatCurrency(val?: number | null): string {
  if (!val) return '—';
  return '$' + val.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function parseCSV(text: string): Record<string, string>[] {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return [];
  const headers = lines[0].split(',').map((h) => h.trim().replace(/^"|"$/g, ''));
  return lines.slice(1).map((line) => {
    const vals = line.split(',').map((v) => v.trim().replace(/^"|"$/g, ''));
    const row: Record<string, string> = {};
    headers.forEach((h, i) => { row[h] = vals[i] || ''; });
    return row;
  });
}

export default function CountyTransfers() {
  const [tab, setTab] = useState<'import' | 'browse' | 'stats'>('browse');

  // Import state
  const [county, setCounty] = useState('San Bernardino');
  const [csvRows, setCsvRows] = useState<Record<string, string>[]>([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);

  // Browse state
  const [transfers, setTransfers] = useState<CountyTransferRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState({
    county: '', is_industrial: false, search: '',
    date_from: '', date_to: '', min_price: '', max_price: '',
  });

  // Stats state
  const [stats, setStats] = useState<Record<string, any>>({});

  const loadTransfers = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = { limit: '50', offset: String(page * 50) };
      if (filters.county) params.county = filters.county;
      if (filters.is_industrial) params.is_industrial = 'true';
      if (filters.search) params.search = filters.search;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.min_price) params.min_price = filters.min_price;
      if (filters.max_price) params.max_price = filters.max_price;
      const res = await api.listCountyTransfers(params);
      setTransfers(res.transfers);
      setTotal(res.total);
    } catch (err) {
      console.error('Failed to load transfers:', err);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => { if (tab === 'browse') loadTransfers(); }, [tab, loadTransfers]);

  useEffect(() => {
    if (tab === 'stats') {
      api.getCountyTransferStats().then((r) => setStats(r.stats || {})).catch(console.error);
    }
  }, [tab]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      setCsvRows(parseCSV(text));
      setImportResult(null);
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (csvRows.length === 0) return;
    setImporting(true);
    try {
      const res = await api.importCountyTransfers({ rows: csvRows, county });
      setImportResult(res);
      setCsvRows([]);
    } catch (err: any) {
      setImportResult({ error: err.message });
    } finally {
      setImporting(false);
    }
  };

  const handlePush = async (id: string) => {
    try {
      await api.pushTransferToPool(id);
      loadTransfers();
    } catch (err) {
      console.error('Push failed:', err);
    }
  };

  const handleBatchPush = async () => {
    if (selected.size === 0) return;
    try {
      await api.batchPushTransfers(Array.from(selected));
      setSelected(new Set());
      loadTransfers();
    } catch (err) {
      console.error('Batch push failed:', err);
    }
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const tabs = [
    { key: 'browse' as const, label: 'Browse' },
    { key: 'import' as const, label: 'Import' },
    { key: 'stats' as const, label: 'Stats' },
  ];

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-[28px] font-semibold uppercase leading-[1.06] tracking-[-.01em] text-gray-900 sm:text-[32px]">County Transfers</h1>
            <p className="text-sm text-gray-500 mt-1">Import and track county transfer records for comp pool enrichment</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex gap-6">
            {tabs.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                  tab === t.key
                    ? 'border-primary-500 text-primary-500'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {t.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Import Tab */}
        {tab === 'import' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl border p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">County</label>
                  <select
                    value={county}
                    onChange={(e) => setCounty(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    {COUNTIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CSV File</label>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileUpload}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm file:mr-4 file:py-1 file:px-3 file:rounded file:border-0 file:text-sm file:bg-primary-50 file:text-primary-500"
                  />
                </div>
              </div>

              {csvRows.length > 0 && (
                <>
                  <p className="text-sm text-gray-600">{csvRows.length} rows parsed. Preview (first 10):</p>
                  <div className="overflow-x-auto border rounded-lg">
                    <table className="min-w-full text-xs">
                      <thead className="bg-gray-50">
                        <tr>
                          {Object.keys(csvRows[0]).slice(0, 8).map((h) => (
                            <th key={h} className="px-3 py-2 text-left font-medium text-gray-600">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {csvRows.slice(0, 10).map((row, i) => (
                          <tr key={i}>
                            {Object.keys(csvRows[0]).slice(0, 8).map((h) => (
                              <td key={h} className="px-3 py-1.5 text-gray-700 whitespace-nowrap">{row[h]}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <button
                    onClick={handleImport}
                    disabled={importing}
                    className="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 disabled:opacity-50"
                  >
                    {importing ? 'Importing...' : `Import ${csvRows.length} Records`}
                  </button>
                </>
              )}

              {importResult && !importResult.error && (
                <div className="bg-success-wash border border-success/30 rounded-lg p-4">
                  <p className="text-sm font-medium text-success">Import Complete</p>
                  <p className="text-sm text-success mt-1">
                    {importResult.stats?.total_imported} records imported •
                    {importResult.stats?.industrial_count} industrial •
                    {importResult.stats?.matched_to_properties} matched to properties
                  </p>
                </div>
              )}

              {importResult?.error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-700">{importResult.error}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Browse Tab */}
        {tab === 'browse' && (
          <div className="space-y-4">
            {/* Filters */}
            <div className="bg-white rounded-xl border p-4">
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                <select
                  value={filters.county}
                  onChange={(e) => { setFilters((f) => ({ ...f, county: e.target.value })); setPage(0); }}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">All Counties</option>
                  {COUNTIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={filters.is_industrial}
                    onChange={(e) => { setFilters((f) => ({ ...f, is_industrial: e.target.checked })); setPage(0); }}
                    className="rounded"
                  />
                  Industrial Only
                </label>
                <input
                  type="text"
                  placeholder="Search..."
                  value={filters.search}
                  onChange={(e) => { setFilters((f) => ({ ...f, search: e.target.value })); setPage(0); }}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
                <input type="date" value={filters.date_from} onChange={(e) => { setFilters((f) => ({ ...f, date_from: e.target.value })); setPage(0); }} className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                <input type="date" value={filters.date_to} onChange={(e) => { setFilters((f) => ({ ...f, date_to: e.target.value })); setPage(0); }} className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                <input type="number" placeholder="Min Price" value={filters.min_price} onChange={(e) => { setFilters((f) => ({ ...f, min_price: e.target.value })); setPage(0); }} className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                <input type="number" placeholder="Max Price" value={filters.max_price} onChange={(e) => { setFilters((f) => ({ ...f, max_price: e.target.value })); setPage(0); }} className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
            </div>

            {/* Batch actions */}
            {selected.size > 0 && (
              <div className="flex items-center gap-3 bg-primary-50 rounded-lg px-4 py-2">
                <span className="text-sm text-primary-500">{selected.size} selected</span>
                <button onClick={handleBatchPush} className="px-3 py-1.5 bg-primary-500 text-white rounded-lg text-xs font-medium hover:bg-primary-600">
                  Push Selected to Pool
                </button>
                <button onClick={() => setSelected(new Set())} className="text-xs text-gray-500 hover:text-gray-700">Clear</button>
              </div>
            )}

            {/* Table */}
            <div className="bg-white rounded-xl border overflow-hidden">
              {loading ? (
                <div className="p-8 text-center text-gray-400">Loading...</div>
              ) : transfers.length === 0 ? (
                <div className="p-8 text-center text-gray-400">No transfer records found</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-3 py-3 text-left w-8">
                          <input type="checkbox" className="rounded" onChange={(e) => {
                            if (e.target.checked) setSelected(new Set(transfers.map((t) => t.id)));
                            else setSelected(new Set());
                          }} />
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Address</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">City</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Grantor → Grantee</th>
                        <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Sale Price</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Land Use</th>
                        <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">Type</th>
                        <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">Pool</th>
                        <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {transfers.map((t) => (
                        <tr key={t.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2">
                            <input type="checkbox" checked={selected.has(t.id)} onChange={() => toggleSelect(t.id)} className="rounded" />
                          </td>
                          <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{t.transfer_date || '—'}</td>
                          <td className="px-3 py-2 text-gray-900 font-medium">{t.property_address || '—'}</td>
                          <td className="px-3 py-2 text-gray-600">{t.city || '—'}</td>
                          <td className="px-3 py-2 text-gray-600 text-xs">
                            {t.grantor || '?'} → {t.grantee || '?'}
                          </td>
                          <td className="px-3 py-2 text-right text-gray-900 font-medium">{formatCurrency(t.calculated_sale_price)}</td>
                          <td className="px-3 py-2 text-gray-500 text-xs">{t.land_use_description || t.land_use_code || '—'}</td>
                          <td className="px-3 py-2 text-center">
                            {t.is_industrial ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-success-wash text-success">IND</span>
                            ) : (
                              <span className="text-gray-300">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {t.pushed_to_pool ? (
                              <svg className="w-5 h-5 text-success mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            ) : t.matched_property_id ? (
                              <svg className="w-5 h-5 text-info mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101" />
                              </svg>
                            ) : null}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {!t.pushed_to_pool && (
                              <button
                                onClick={() => handlePush(t.id)}
                                className="text-xs text-primary-500 hover:text-primary-600 font-medium"
                              >
                                Push
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Pagination */}
            {total > 50 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">
                  Showing {page * 50 + 1}–{Math.min((page + 1) * 50, total)} of {total}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="px-3 py-1.5 border rounded-lg text-sm disabled:opacity-50 hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={(page + 1) * 50 >= total}
                    className="px-3 py-1.5 border rounded-lg text-sm disabled:opacity-50 hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Stats Tab */}
        {tab === 'stats' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Object.entries(stats).length === 0 ? (
              <div className="col-span-2 text-center text-gray-400 py-12">No transfer data yet. Import records to see stats.</div>
            ) : (
              Object.entries(stats).map(([countyName, s]: [string, any]) => (
                <div key={countyName} className="bg-white rounded-xl border p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">{countyName}</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Total Records</p>
                      <p className="text-2xl font-bold text-gray-900">{s.total?.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Industrial</p>
                      <p className="text-2xl font-bold text-success">{s.industrial?.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Matched</p>
                      <p className="text-2xl font-bold text-info">{s.matched?.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Pushed to Pool</p>
                      <p className="text-2xl font-bold text-gray-800">{s.pushed?.toLocaleString()}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs text-gray-500 uppercase">Total Volume</p>
                      <p className="text-2xl font-bold text-gray-900">{formatCurrency(s.total_volume)}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
